import {
  difference,
  differenceWith,
  forEach,
  get,
  isEmpty,
  isEqual,
  keyBy,
  last,
  map,
  some,
  uniqBy,
  values,
} from "lodash"
import { Action } from "redux"
import { TakeableChannel } from "redux-saga"
import { ActionCreators } from "redux-undo"
import moment from "moment"
import { call, put, select, takeLatest } from "redux-saga/effects"
import ActionTypes from "~/enums/ActionTypes"
import { getSimulationId, getState, getUserData } from "../selectors"
import { checkTokenAndRedirect, doAPIRequest } from "../api"
import { post } from "~/utils/http"
import { createAction } from "~/utils/redux"
import { API_SERVER_PLANNING_PROCESS } from "~/constants/api"
import { timelineRerender } from "../../actions/ui/timelines"
import { filterCleverly, setTimelineUpdateLoading, unsetTimelineUpdateLoading } from "../../actions/timelines"
import errors from "~/utils/errors"
import { createSnackbarMessage } from "../../actions/ui/snackbar"
import MessageTypes from "~/enums/MessageTypes"
import { FilterDateRange, TimelinePlanningObject, TimelineSagaResponse, TimelineSagaState } from "./TimelineSagaTypes"
import { AuthenticationState } from "~/types/ReduxStateExtensions"
import { addOwner } from "~/reducers/timeline/helpers/addOwner"
import { User } from "~/types/api/UserTypes"

interface UpdateObjectProps {
  activities?: object[]
  operations?: object[]
  orderPositions?: object[]
  productionOrders?: object[]
  projects?: object[]
  valueAdds?: object[]
}

interface UpdateObjectVersion extends UpdateObjectProps {
  versionIdentifier?: number
}

interface CheckDateRangeRestrictionProps {
  updateObj: UpdateObjectVersion
  filterDateRange: FilterDateRange
}

function diffProjects(present: object[], past: object[], affectedProjectIds: (string | number)[] = []): object[] {
  const directChanges = differenceWith(
    present,
    past,
    (a: TimelinePlanningObject, b: TimelinePlanningObject) => a.projectId === b.projectId && isEqual(a, b)
  )

  if (isEmpty(affectedProjectIds)) {
    return directChanges
  }

  const affectedSet = new Set(affectedProjectIds)
  const indirectChanges = present.filter((item: TimelinePlanningObject) => affectedSet.has(item.projectId))

  return uniqBy([...directChanges, ...indirectChanges], "projectId")
}

function getGroupChanges(
  present: Record<string, string>[],
  past: object[],
  idKey: string
): { diff: object[]; ids: (string | number)[] } {
  const diff: object[] = []
  const ids = new Set<string | number>()
  const pastMap = keyBy(past, idKey)
  forEach(present, item => {
    const id = item[idKey]
    const old = pastMap[id]

    if (old === undefined || !isEqual(item, old)) {
      diff.push(item)
      if (item.projectId) {
        ids.add(item.projectId)
      }
    }
  })
  return { diff, ids: Array.from(ids) }
}

const groups: Array<keyof UpdateObjectProps> = [
  "activities",
  "operations",
  "orderPositions",
  "productionOrders",
  "projects",
  "valueAdds",
]

const idKeyMap: Partial<Record<keyof UpdateObjectProps, string>> = {
  activities: "activityId",
  operations: "operationId",
  orderPositions: "orderPositionId",
  productionOrders: "productionOrderId",
  projects: "projectId",
  valueAdds: "valueAddId",
}

const endPoint = `${API_SERVER_PLANNING_PROCESS}/planningTimelineRaw`

function calculateUpdatePayload(
  presentTimeline: Record<string, object>,
  comparisonTimeline: Record<string, object>
): { updateObj: UpdateObjectVersion; hasChanges: boolean } {
  const updateObj: UpdateObjectVersion = {}
  let hasChanges = false
  const affectedProjectIds = new Set<number | string>()
  const nonProjectGroups: Array<keyof UpdateObjectProps> = groups.filter(group => group !== "projects")

  forEach(nonProjectGroups, group => {
    const presentValues = values(presentTimeline[group])
    const comparisonValues = values(comparisonTimeline[group])
    const idKey = idKeyMap[group]

    const { diff, ids } = getGroupChanges(presentValues, comparisonValues, idKey)

    updateObj[group] = diff

    if (!isEmpty(diff)) {
      hasChanges = true
      ids.forEach(id => affectedProjectIds.add(id))
    }
  })

  const presentProjects = values(presentTimeline.projects)
  const comparisonProjects = values(comparisonTimeline.projects)

  updateObj.projects = diffProjects(presentProjects, comparisonProjects, Array.from(affectedProjectIds))

  if (!isEmpty(updateObj.projects)) {
    hasChanges = true
  }

  return { updateObj, hasChanges }
}

const checkDateRangeRestriction = ({ updateObj, filterDateRange }: CheckDateRangeRestrictionProps) => {
  const productionsOrders = updateObj.productionOrders as { startDate: string; endDate: string }[]
  if (isEmpty(productionsOrders)) {
    return false
  }
  const { fromDate, toDate } = filterDateRange
  return some(productionsOrders, ({ startDate, endDate }) => {
    const outOfStartDate = moment(startDate).isBefore(fromDate)
    const outOfEndDate = moment(endDate).isAfter(toDate)
    return outOfStartDate || outOfEndDate
  })
}

function* saveOrUndo(action: { payload: Action<object> }) {
  const { payload: subAction } = action

  const state = (yield select(getState)) as TimelineSagaState
  const { selection } = get(state, "timeline.present")
  if (!selection || !selection.length) {
    yield put(timelineRerender())
    return
  }

  yield put(setTimelineUpdateLoading())
  try {
    yield put(subAction)
  } catch (err) {
    if (err instanceof errors.UserError) {
      const userError = err as { messageId: number; values: object[]; message: string }
      yield put(createSnackbarMessage(MessageTypes.ERROR, userError.messageId, userError.values, userError.message))
    } else {
      yield put(createSnackbarMessage(MessageTypes.ERROR, "pages.timeline.updateError"))
    }

    yield put(unsetTimelineUpdateLoading())
    yield put(createAction(ActionTypes.TIMELINE_TRIGGER_RAW))
    yield put(timelineRerender())
    return
  }
  const { timeline, ui } = (yield select(getState)) as TimelineSagaState
  const { token } = (yield select(getUserData)) as AuthenticationState
  const simulationId = (yield select(getSimulationId)) as number

  if (isEmpty(last(timeline.past))) {
    yield put(timelineRerender())
    yield put(unsetTimelineUpdateLoading())
    return
  }
  /* Detect the changed data items and send them to the API */
  const { updateObj } = calculateUpdatePayload(timeline.present, last(timeline.past))
  updateObj.versionIdentifier = ui.timelines.versionIdentifier

  const { filterDateRange } = ui.timelines
  if (checkDateRangeRestriction({ updateObj, filterDateRange })) {
    yield put(createSnackbarMessage(MessageTypes.WARNING, "pages.timeline.moveOperation.error"))
    yield put(unsetTimelineUpdateLoading())
    yield put(ActionCreators.undo())
    return
  }
  let resp = null
  try {
    resp = (yield doAPIRequest({
      endPoint,
      method: post,
      prepareRequest: () => updateObj,
      token,
      state: { simulation: { id: simulationId } },
      payload: null,
      prepareResponse: null,
      prepareUrl: null,
    })) as TimelineSagaResponse

    const { error, projects: fetchedProjects, versionIdentifier } = resp

    if (error) {
      switch (error) {
        case "TimelineDataOutdated": {
          yield put(createSnackbarMessage(MessageTypes.ERROR, "errors.API.TimelineDataOutdated"))
          yield put(ActionCreators.undo())
          yield put(ActionCreators.clearHistory())
          break
        }
        default: {
          yield put(ActionCreators.undo())
        }
      }
    }

    const updatedProjects = difference(map(resp.projects, "projectId"), map(updateObj.projects, "projectId"))

    const cleverFilterId: unknown = get(timeline, "present.filters.clever")

    if (cleverFilterId) {
      yield put(filterCleverly(cleverFilterId, true))
    }

    yield put(unsetTimelineUpdateLoading())

    const users: User[] = get(state, "entities.users")
    const projects = addOwner(fetchedProjects, users)

    yield put(
      createAction(ActionTypes.TIMELINE_UPDATE_PROJECTS, {
        projects,
        affectedProjects: updatedProjects,
        users,
      })
    )
    yield put(
      createAction(ActionTypes.TIMELINE_VERSION_UPDATE, {
        versionIdentifier,
      })
    )
  } catch (e) {
    const skipErrorHandling: unknown = yield call(checkTokenAndRedirect, e)
    if (skipErrorHandling) {
      return
    }

    yield put(unsetTimelineUpdateLoading())
    yield put(ActionCreators.undo())
    return
  }

  yield put(
    createAction(ActionTypes.ENTITIES_UPDATE_PERSISTS, {
      ...updateObj,
    })
  )
}

function* undoUpdate() {
  let indexInFuture = 0
  yield put(setTimelineUpdateLoading())
  yield put(ActionCreators.undo())
  const stateUndo = (yield select(getState)) as TimelineSagaState

  if (!isEmpty(get(stateUndo.timeline, "present.operationsResourcesSuggestions"))) {
    yield put(ActionCreators.undo())
    indexInFuture = 1
  }
  const { timeline, ui } = (yield select(getState)) as TimelineSagaState
  const { token } = (yield select(getUserData)) as AuthenticationState
  const simulationId = (yield select(getSimulationId)) as number

  const currentTimeline = timeline.future[indexInFuture]
  /* Detect the changed data items and send them to the API */
  const { updateObj, hasChanges } = calculateUpdatePayload(timeline.present, currentTimeline)

  if (!hasChanges) {
    yield put(unsetTimelineUpdateLoading())
    return // nothing to do here, probably a selection was undone
  }

  updateObj.versionIdentifier = ui.timelines.versionIdentifier

  let resp = null

  try {
    resp = (yield doAPIRequest({
      endPoint,
      method: post,
      prepareRequest: () => updateObj,
      token,
      state: { simulation: { id: simulationId } },
      payload: null,
      prepareResponse: null,
      prepareUrl: null,
    })) as TimelineSagaResponse

    const { error, projects: fetchedProjects, versionIdentifier } = resp

    if (error) {
      switch (error) {
        case "TimelineDataOutdated": {
          yield put(createSnackbarMessage(MessageTypes.ERROR, "errors.API.TimelineDataOutdated"))
          yield put(ActionCreators.undo())
          yield put(ActionCreators.clearHistory())
          break
        }
        default: {
          yield put(ActionCreators.undo())
        }
      }
    }

    const updatedProjects = difference(map(resp.projects, "projectId"), map(updateObj.projects, "projectId"))

    const users: User[] = get(stateUndo, "entities.users")
    const projects = addOwner(fetchedProjects, users)

    yield put(unsetTimelineUpdateLoading())
    yield put(
      createAction(ActionTypes.TIMELINE_UPDATE_PROJECTS, {
        projects,
        affectedProjects: updatedProjects,
        users,
      })
    )
    yield put(
      createAction(ActionTypes.TIMELINE_VERSION_UPDATE, {
        versionIdentifier,
      })
    )
  } catch (e) {
    const skipErrorHandling: unknown = yield call(checkTokenAndRedirect, e)
    if (skipErrorHandling) {
      return
    }
    yield put(ActionCreators.redo())
    yield put(unsetTimelineUpdateLoading())
    return
  }

  yield put(createAction(ActionTypes.TIMELINE_ACTIVITY_MOVE_PREVIEW_CLEAR))
  yield put(createAction(ActionTypes.TIMELINE_OPERATION_MOVE_PREVIEW_CLEAR))

  yield put(
    createAction(ActionTypes.ENTITIES_UPDATE_PERSISTS, {
      ...updateObj,
    })
  )
}

function* cloneOrUndo(action: { payload: { item: { itemId: number; end: string; start: string } } }) {
  const { timeline } = (yield select(getState)) as TimelineSagaState
  const { token } = (yield select(getUserData)) as AuthenticationState
  const simulationId = (yield select(getSimulationId)) as number
  const {
    payload: { item },
  } = action
  const currentActivity = timeline.present.activities[item.itemId]

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const returnedActivity: { activityId: number } = yield doAPIRequest({
    endPoint: `${endPoint}/activity/copy`,
    method: post,
    prepareRequest: () => ({
      ...currentActivity,
      endDate: moment(item.end).format(),
      startDate: moment(item.start).format(),
    }),
    token,
    state: { simulation: { id: simulationId } },
    payload: null,
    prepareResponse: null,
    prepareUrl: null,
  })

  if (returnedActivity) {
    yield put(
      createAction(ActionTypes.TIMELINE_ITEM_CLONED, {
        activity: returnedActivity,
      })
    )

    /* Apply the standard code which makes parental items grow to items size */
    yield put(
      createAction(ActionTypes.TIMELINE_ITEM_UPDATE, {
        type: ActionTypes.TIMELINE_ACTIVITY_RESIZE,
        payload: {
          item: {
            ...item,
            activityId: returnedActivity.activityId,
            itemId: returnedActivity.activityId,
          },
        },
      })
    )
  }
}

export default [
  takeLatest(ActionTypes.TIMELINE_ITEM_UPDATE as unknown as TakeableChannel<unknown>, saveOrUndo),
  takeLatest(ActionTypes.TIMELINE_ITEM_UNDO_UPDATE, undoUpdate),
  takeLatest(ActionTypes.TIMELINE_ITEM_CLONE as unknown as TakeableChannel<unknown>, cloneOrUndo),
]
